import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './EngagementClosingDate.scss';
import { useStateSelector } from '../../../store/selectors';
import Helpers from '../../../utils/helper';
import { FetchEngagementClosingDate } from '../../../slices/marketplace/closing-date/thunks/fetch-engagement-closing-date.thunk';
import { ClosingDateProposalStatus } from '../../../slices/marketplace/closing-date/models/engagement-closing-date.model';
import { ClosingDateCard } from './ClosingDateCard';
import { ClosingDatePartyChips } from './ClosingDatePartyChips';
import { ClosingDateConfirmedBanner } from './ClosingDateConfirmedBanner';
import { ClosingDateGuidanceAlert } from './ClosingDateGuidanceAlert';
import { ClosingDateUnresolvedWarning } from './ClosingDateUnresolvedWarning';
import { ClosingDateSchedulingBlock } from './ClosingDateSchedulingBlock';
import { ClosingDateActionControls } from './ClosingDateActionControls';
import { ClosingDateCloseConfirmationSection } from './ClosingDateCloseConfirmationSection';

export interface EngagementClosingDateProps {
    engagementId: number;
}

export const EngagementClosingDate = (props: EngagementClosingDateProps) => {
    const dispatch = useDispatch() as any;
    const closingDate = useStateSelector(
        (state) => state.closingDateState.closingDate
    );
    const isLoading = useStateSelector(
        (state) => state.closingDateState.isLoading
    );

    useEffect(() => {
        if (props.engagementId) {
            dispatch(FetchEngagementClosingDate(props.engagementId));
        }
    }, [props.engagementId, dispatch]);

    if (isLoading || !closingDate) {
        return Helpers.renderTableLoader();
    }

    const isCurrentProposalAccepted =
        closingDate.currentProposal?.status ===
        ClosingDateProposalStatus.Accepted;
    const isCurrentProposalPending =
        closingDate.currentProposal?.status ===
        ClosingDateProposalStatus.Pending;
    const canConfirmCloseSection =
        !!closingDate.officialClosingDate && !isCurrentProposalPending;
    const showsNegotiationState = !closingDate.isEngagementClosed;

    return (
        <div className="engagement-closing-date">
            <div className="engagement-closing-date__card card">
                {closingDate.hasUnresolvedFlowOfFundsItems && (
                    <ClosingDateUnresolvedWarning />
                )}
                {isCurrentProposalAccepted && <ClosingDateConfirmedBanner />}
                <ClosingDateCard closingDate={closingDate} />
                {showsNegotiationState && closingDate.currentProposal && (
                    <ClosingDatePartyChips
                        responses={closingDate.currentProposal.responses}
                        currentUserParty={closingDate.currentUserParty}
                    />
                )}
                {showsNegotiationState && (
                    <ClosingDateGuidanceAlert
                        isConfirmed={isCurrentProposalAccepted}
                    />
                )}
                <ClosingDateActionControls
                    engagementId={props.engagementId}
                    canPropose={closingDate.canPropose}
                    canRespond={closingDate.canRespond}
                    proposalId={closingDate.currentProposal?.id}
                />
                {canConfirmCloseSection && (
                    <ClosingDateCloseConfirmationSection
                        engagementId={props.engagementId}
                        closeConfirmations={closingDate.closeConfirmations}
                        currentUserParty={closingDate.currentUserParty}
                        canConfirmClose={closingDate.canConfirmClose}
                        officialClosingDate={closingDate.officialClosingDate}
                    />
                )}
            </div>
            <ClosingDateSchedulingBlock
                engagementId={props.engagementId}
                threadedDiscussionId={closingDate.threadedDiscussionId}
                commsId={closingDate.commsId}
                currentUserParty={closingDate.currentUserParty}
                isEngagementClosed={closingDate.isEngagementClosed}
            />
        </div>
    );
};
